import React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckedState } from '@radix-ui/react-checkbox';
import CheckIcon from '../../../images/check-icon.svg';
import { useFormProvider } from '../form-provider';
import FormAlert from '../form-alert';
import usePersistedId from '../utils/use-persisted-id';
import { colors } from '../colors';
import { borderRadius } from '../tokens';

export interface CheckBoxProps
    extends Omit<
        HtmlPropsNoRef<HTMLButtonElement>,
        'onChange' | 'type' | 'size' | 'value'
    > {
    /**
    * Name of the CheckBox - will be used for the form validation if using FormContext so make sure it's unique.
    */
    name: string;
    /**
    * Children refers to the label
    * */
    children: any;
    /**
    * The size of the radio button.
    * */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
    * The font-size of the label.
    */
    LabelFontSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
    * Make the field required or not. Defaults to false for all field types.
    */
    required?: boolean;
    /**
    * Explicitly control the state of the element
    */
    checked?: boolean;
    /**
    * Should the field be checked by default, if uncontrolled?
    */
    defaultChecked?: boolean;
    /**
    * Make the field disabled or not. Defaults to false for all field types.
    */
    disabled?: boolean;
    /**
    * This refers to the value of the input
    */
    value?: boolean;
    /*
    * onChange event
    * */
    onChange?(checked: CheckedState): void;
    /*
    * Style Inputs
    * */
    inputBoxStyle?: string;
}

export const CheckBox = ({
    name,
    children,
    size = 'sm',
    LabelFontSize = 'md',
    required = false,
    defaultChecked = false,
    // onBlur,
    // disabled,
    value,
    onChange,
    onBlur,
    disabled,
}: CheckBoxProps): JSX.Element => {
    /*
    * Get our form provider. It may not exist
    * (if the input component is not inside a FormProvider and is using the component separately)
    * so make sure to not access it directly without first checking.
    * */
    const formContext = useFormProvider();

    const foundSize = {
        xs: '0.75rem',
        sm: '1rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '3rem',
    }[size];

    const innerSize = {
        xs: '0.75rem',
        sm: '1rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '3rem',
    }[size];

    const labelSize = {
        xs: '0.7rem',
        sm: '0.85rem',
        md: '1rem',
        lg: '1.25rem',
        xl: '1.5rem',
    }[LabelFontSize];

    const id = usePersistedId();

    /*
    * Set up our register function to be react-hook-form if the context exists,
    * if it doesn't, pass the props we destructed back to the element
    * */
    const registerFn = formContext && formContext.register
        ? formContext.register(name, {
            required,
            onBlur,
            disabled,
            onChange
        })
        : {
            required,
            onBlur,
            disabled,
        };

    React.useEffect(() => {
        if (formContext) {
            formContext.setValue(name, defaultChecked)
        }
    }, [defaultChecked])

    return (
        <>
            <div css={{ paddingBottom: 4 }}>
                <div css={{ alignItems: 'center', display: 'flex' }}>
                    <CheckboxPrimitive.Root
                        css={{
                            all: 'unset',
                            // backgroundColor: `${value ? '#174874' : '#FFFFFF'}`,
                            border: `2px solid ${formContext?.errors[name] ? colors.statesError : colors.grey300}`,
                            minWidth: `${foundSize} !important`,
                            minHeight: `${foundSize} !important`,
                            borderRadius: borderRadius.small,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ['&:focus-visible']: {
                                boxShadow: `0 0 0 2px ${colors.statesFocus}`,
                                backgroundColor: colors.grey300,
                            },
                        }}
                        aria-invalid={
                            formContext && formContext.errors && formContext.errors[name]
                                ? 'true'
                                : 'false'
                        }
                        id={id}
                        onCheckedChange={(e) => {
                            if ('onChange' in registerFn) {
                                const event = { target: { name: name, value: e } };
                                registerFn.onChange(event);
                            }
                            if (onChange) {
                                onChange(e)
                            }
                        }
                        }
                        defaultChecked={defaultChecked}
                        // checked={checked}
                        // {...formContext?.register(name, {
                        // required: required,
                        // })}
                        {...registerFn}
                    >
                        <CheckboxPrimitive.Indicator
                            css={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                display: 'flex',
                            }}
                        >
                            <CheckIcon width={innerSize} height={innerSize} />
                        </CheckboxPrimitive.Indicator>
                    </CheckboxPrimitive.Root>
                    {required && <span css={{ paddingLeft: 4 }}>*</span>}
                    <label
                        css={{
                            color: '#444444',
                            fontSize: `${labelSize}`,
                            lineHeight: '27px',
                            fontWeight: 400,
                            userSelect: 'none',
                            paddingLeft: 10,
                        }}
                        htmlFor={id}
                    >
                        {children}
                    </label>
                </div>
            </div>
            {formContext?.errors[name]?.type === 'required' && (
                <FormAlert
                    style={{ marginTop: 0 }}
                >Required</FormAlert>
            )}
        </>
    );
};

export default CheckBox